import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import FilterIcon from "@atlaskit/icon/core/filter";
import GrowDiagonalIcon from "@atlaskit/icon/core/grow-diagonal";
import ShrinkDiagonalIcon from "@atlaskit/icon/core/shrink-diagonal";
import {token} from "@atlaskit/tokens";
import {useMediaQuery} from "@mantine/hooks";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useCallback, useMemo, useState, useEffect} from "react";
import type {ListNotesPanelContext, ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";
import {NoteDeleteConfirmationModal} from "~/components/notes/noteDeleteConfirmationModal";
import {NoteFeedItem} from "~/components/notes/noteFeedItem";
import {createEmptyComposerId, NotesComposer} from "~/components/notes/notesComposer";
import {ProfileCustomerNotesLifecycleSelect} from "~/components/entityProfile/notes/profileCustomerNotesLifecycleSelect";
import {NotesAddNoteLabel, NotesPanelTitle, NotesPreviousNotesLabel} from "~/components/notes/notesPanelTypography";
import type {NotesLifecycleSelectorConfig} from "~/components/notes/listNotesPanelTypes";
import {buildNoteForSave, deleteNote, saveNote} from "~/components/notes/persistNotes";
import {
    AsideFullscreenPanel,
    AsideSidebarPanel,
    FullscreenModal,
    SidebarModal,
} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {usePlatformUserDetails, useWorkspace} from "~/contexts/workspaceContext";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {
    collectEventContainerNoteListItems,
    computeNotePlainTextLength,
    NOTES_BODY_MAX_PLAIN_LENGTH,
    readEventContainerNotes,
    serializeNotesForApi,
    upsertNoteInList,
    removeNoteFromList,
    type NoteAttachment,
    type NoteListItem,
    type NoteSortDirection,
} from "~/domainModels/notes";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uploadAsset} from "~/utilities/apis/api";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {findWorkspaceUserByStoredReferenceId, getUserDisplayName, getUserRole} from "~/utilities/utilities";
import {isFormValueDirty} from "~/utilities/isFormValueDirty";

const SORT_LABELS: Record<NoteSortDirection, string> = {
    recent: "Recent First",
    oldest: "Oldest First",
};

export function NotesSortControl({sortDirection, onChange}: {sortDirection: NoteSortDirection; onChange: (next: NoteSortDirection) => void}) {
    return (
        <AnankeDropdownMenu<HTMLButtonElement>
            placement="bottom-end"
            trigger={({triggerRef, ...triggerProps}) => (
                <button
                    type="button"
                    {...triggerProps}
                    ref={triggerRef}
                    className="grid grid-cols-[max-content_max-content_max-content] items-center gap-ds-space-075"
                    style={{
                        minHeight: "32px",
                        padding: "0 var(--space-padding-interactive-large, 12px)",
                        borderRadius: "var(--radius-medium, 4px)",
                        border: `1px solid ${token("color.border")}`,
                        background: token("color.background.neutral.subtle"),
                        color: token("color.text"),
                        fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: "var(--Font-Size-Body-M, 14px)",
                        fontWeight: "var(--Font-Weight-Body-Medium, 500)",
                        lineHeight: "var(--Line-Height-Body-M, 20px)",
                        cursor: "pointer",
                    }}
                >
                    <FilterIcon
                        label=""
                        color={token("color.icon")}
                    />
                    <span>{SORT_LABELS[sortDirection]}</span>
                    <ChevronDownIcon
                        label=""
                        color={token("color.icon.subtle")}
                    />
                </button>
            )}
        >
            <AnankeDropdownItemGroup>
                <AnankeDropdownItem
                    isSelected={sortDirection === "recent"}
                    onClick={() => onChange("recent")}
                >
                    Recent First
                </AnankeDropdownItem>
                <AnankeDropdownItem
                    isSelected={sortDirection === "oldest"}
                    onClick={() => onChange("oldest")}
                >
                    Oldest First
                </AnankeDropdownItem>
            </AnankeDropdownItemGroup>
        </AnankeDropdownMenu>
    );
}

function useNoteAuthorResolver() {
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const platformUserDetails = usePlatformUserDetails();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUser) => [platformUser.userId, platformUser])),
        [platformUserDetails],
    );
    return (userId: Uuid) => {
        const user = findWorkspaceUserByStoredReferenceId(userId, users) ?? users.find((item) => item.id === userId);
        const templateId = user?.templateId ?? null;
        return {
            displayName: getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId),
            roleLabel: templateId !== null ? getUserRole(templateId, userTemplates) : "",
            avatarUrl: undefined,
        };
    };
}

export function ListNotesPanelBody({
    context,
    eventContainer,
    onEventContainerPatched,
    isFullscreen,
    showHistory = true,
    showSortControl,
    previousNotesSection = false,
    lifecycleSelector,
    composerFooterPlacement = "inline",
    onNewNoteComposerStateChange,
    onDirtyChange,
}: {
    context: ListNotesPanelOpenContext;
    eventContainer: OttopilotEventContainer;
    onEventContainerPatched: (eventContainer: OttopilotEventContainer) => void;
    isFullscreen: boolean;
    showHistory?: boolean;
    showSortControl?: boolean;
    /** Customer profile: section header + sort row above the feed (instead of sort-only at top). */
    previousNotesSection?: boolean;
    /** Customer profile: required lifecycle picker above the composer. */
    lifecycleSelector?: NotesLifecycleSelectorConfig;
    composerFooterPlacement?: "inline" | "external";
    onNewNoteComposerStateChange?: (state: {
        plainLength: number;
        canSave: boolean;
        isSaving: boolean;
        isUploading: boolean;
        onSave: () => void;
    }) => void;
    /** Reports whether the composer / edit draft currently holds unsaved edits. */
    onDirtyChange?: (isDirty: boolean) => void;
}) {
    const {workspace, workspaceCapabilities} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {showFlag} = useAnankeFlags();
    const eventTemplates = useEventTemplates();
    const queryClient = useQueryClient();
    const resolveAuthor = useNoteAuthorResolver();

    const [sortDirection, setSortDirection] = useState<NoteSortDirection>("recent");
    const [composerMarkdown, setComposerMarkdown] = useState("");
    const [composerAttachments, setComposerAttachments] = useState<Array<NoteAttachment>>([]);
    const [composerId] = useState(() => createEmptyComposerId());
    const [composerResetKey, setComposerResetKey] = useState(0);
    const [editingNoteId, setEditingNoteId] = useState<Uuid | null>(null);
    const [editMarkdown, setEditMarkdown] = useState("");
    const [editAttachments, setEditAttachments] = useState<Array<NoteAttachment>>([]);
    const [editComposerId] = useState(() => createEmptyComposerId());
    const [isUploadingComposer, setIsUploadingComposer] = useState(false);
    const [isUploadingEdit, setIsUploadingEdit] = useState(false);
    const [notePendingDelete, setNotePendingDelete] = useState<NoteListItem | null>(null);

    const noteItems = useMemo(
        () =>
            collectEventContainerNoteListItems({
                eventContainer,
                eventTemplates,
                resolveAuthor,
                eventContainerSourceLabel: context.eventContainerSourceLabel,
                sortDirection,
            }),
        [eventContainer, eventTemplates, resolveAuthor, context.eventContainerSourceLabel, sortDirection],
    );

    const invalidate = async () => {
        await queryClient.invalidateQueries();
    };

    const uploadFiles = async (
        files: FileList | null,
        onStaged: (updater: (prev: Array<NoteAttachment>) => Array<NoteAttachment>) => void,
        setUploading: (value: boolean) => void,
    ) => {
        if (files === null || files.length === 0) {
            return;
        }
        setUploading(true);
        try {
            const uploaded: Array<NoteAttachment> = [];
            const now = new Date().toISOString() as Iso8601DateTime;
            for (const file of Array.from(files)) {
                const result = await uploadAsset({
                    file,
                    workspaceId: workspace.id,
                    intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                });
                if (result.success !== true) {
                    throw result.error;
                }
                uploaded.push({
                    assetId: result.data.id,
                    fileName: file.name,
                    mimeType: file.type !== "" ? file.type : undefined,
                    createdAt: now,
                });
            }
            onStaged((prev) => [...prev, ...uploaded]);
        } finally {
            setUploading(false);
        }
    };

    const saveMutation = useMutation({
        mutationFn: async (args: {markdown: string; attachments: Array<NoteAttachment>; existing?: NoteListItem}) => {
            const note = buildNoteForSave({
                eventContainerId: context.eventContainerId,
                createScope:
                    args.existing !== undefined
                        ? args.existing.scopeType === "event"
                            ? {
                                  scopeType: "event",
                                  eventId: args.existing.eventId,
                                  eventTemplateId: args.existing.eventTemplateId,
                              }
                            : {scopeType: "eventContainer"}
                        : context.createScope,
                bodyMarkdown: args.markdown,
                attachments: args.attachments,
                createdBy: workspaceUser.userId,
                existingNote: args.existing,
            });
            return await saveNote({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl,
                workspaceId: workspace.id,
                workspaceCapabilities,
                eventContainer,
                note,
            });
        },
        onSuccess: async (savedNote) => {
            const nextEventContainerNotes = upsertNoteInList(readEventContainerNotes(eventContainer), savedNote);
            onEventContainerPatched({
                ...eventContainer,
                notes: serializeNotesForApi(nextEventContainerNotes),
            });
            setComposerMarkdown("");
            setComposerAttachments([]);
            setComposerResetKey((current) => current + 1);
            setEditingNoteId(null);
            setEditMarkdown("");
            setEditAttachments([]);
            await invalidate();
            showFlag({
                title: "Note saved",
                appearance: "success",
                isAutoDismiss: true,
            });
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Could not save note",
                description: error instanceof Error ? error.message : "Unknown error",
                appearance: "error",
                isAutoDismiss: true,
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (note: NoteListItem) => {
            await deleteNote({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl,
                workspaceId: workspace.id,
                workspaceCapabilities,
                eventContainer,
                note,
            });
        },
        onSuccess: async (_data, note) => {
            onEventContainerPatched({
                ...eventContainer,
                notes: serializeNotesForApi(removeNoteFromList(readEventContainerNotes(eventContainer), note.id)),
            });
            setEditingNoteId(null);
            setNotePendingDelete(null);
            await invalidate();
            showFlag({
                title: "Notes has been Deleted successfully",
                appearance: "success",
                isAutoDismiss: true,
            });
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Could not delete note",
                description: error instanceof Error ? error.message : "Unknown error",
                appearance: "error",
                isAutoDismiss: true,
            });
        },
    });
    const savingExistingNoteId = saveMutation.isPending === true ? (saveMutation.variables.existing?.id ?? null) : null;
    const isSavingNewComposer = saveMutation.isPending === true && saveMutation.variables.existing === undefined;

    const resolvedShowSortControl = showSortControl ?? showHistory;
    const showTopSortControl = resolvedShowSortControl === true && previousNotesSection !== true;
    const composerPlainLength = computeNotePlainTextLength(composerMarkdown);
    const composerHasDraft = composerPlainLength > 0 || composerAttachments.length > 0;
    const composerIsOverLimit = composerPlainLength > NOTES_BODY_MAX_PLAIN_LENGTH;
    const composerCanSave =
        isSavingNewComposer === false
        && isUploadingComposer === false
        && composerIsOverLimit === false
        && composerHasDraft === true
        && (lifecycleSelector === undefined || lifecycleSelector.value !== null);

    const handleSaveNewNote = useCallback(() => {
        saveMutation.mutate({
            markdown: composerMarkdown,
            attachments: composerAttachments,
        });
    }, [composerAttachments, composerMarkdown, saveMutation]);

    // Report unsaved-draft state upward so the panel/modal shell can guard close.
    const editingItem = editingNoteId !== null ? (noteItems.find((item) => item.id === editingNoteId) ?? null) : null;
    const editHasChanges =
        editingItem !== null
        && isFormValueDirty(
            {markdown: editMarkdown, attachments: editAttachments},
            {markdown: editingItem.bodyMarkdown, attachments: editingItem.attachments},
        );
    const isDirty = composerHasDraft === true || editHasChanges === true;
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    useEffect(() => {
        return () => {
            onDirtyChange?.(false);
        };
    }, [onDirtyChange]);

    useEffect(() => {
        if (composerFooterPlacement !== "external" || onNewNoteComposerStateChange === undefined) {
            return;
        }
        if (context.readOnly === true) {
            onNewNoteComposerStateChange({
                plainLength: composerPlainLength,
                canSave: false,
                isSaving: false,
                isUploading: false,
                onSave: handleSaveNewNote,
            });
            return;
        }
        onNewNoteComposerStateChange({
            plainLength: composerPlainLength,
            canSave: composerCanSave,
            isSaving: isSavingNewComposer,
            isUploading: isUploadingComposer,
            onSave: handleSaveNewNote,
        });
    }, [
        composerAttachments.length,
        composerCanSave,
        composerFooterPlacement,
        composerMarkdown,
        composerPlainLength,
        context.readOnly,
        isSavingNewComposer,
        isUploadingComposer,
        onNewNoteComposerStateChange,
        handleSaveNewNote,
    ]);

    const panelHeightClass =
        composerFooterPlacement === "external"
            ? "min-h-0"
            : isFullscreen === true
              ? "min-h-0"
              : "min-h-[24rem]";
    const panelPaddingClass = composerFooterPlacement === "external" ? "p-0" : "p-ds-space-150";

    return (
        <div className={`grid h-full min-h-0 w-full min-w-0 grid-rows-[max-content_minmax(0,1fr)] gap-ds-space-150 ${panelPaddingClass} ${panelHeightClass}`}>
            {showTopSortControl === true ? (
                <div className="grid justify-items-end">
                    <NotesSortControl
                        sortDirection={sortDirection}
                        onChange={setSortDirection}
                    />
                </div>
            ) : null}
            <div className="grid min-h-0 w-full min-w-0 auto-rows-max content-start gap-ds-space-150 overflow-y-auto">
                {lifecycleSelector !== undefined ? (
                    <ProfileCustomerNotesLifecycleSelect
                        options={lifecycleSelector.options}
                        value={lifecycleSelector.value}
                        onChange={lifecycleSelector.onChange}
                        isDisabled={lifecycleSelector.isDisabled}
                    />
                ) : null}
                {context.readOnly !== true ? (
                    <div className="grid w-full min-w-0 gap-ds-space-050">
                        <NotesAddNoteLabel />
                        <NotesComposer
                            key={composerResetKey}
                            composerId={composerId}
                            value={composerMarkdown}
                            onChange={setComposerMarkdown}
                            stagedAttachments={composerAttachments}
                            onStagedAttachmentsChange={setComposerAttachments}
                            isSaving={isSavingNewComposer}
                            isUploading={isUploadingComposer}
                            onUploadFiles={(files) => void uploadFiles(files, setComposerAttachments, setIsUploadingComposer)}
                            showFooter={composerFooterPlacement === "inline"}
                            onSave={handleSaveNewNote}
                        />
                    </div>
                ) : null}
                {showHistory === true && noteItems.length === 0 ? (
                    <AnankeText color="color.text.subtlest">No notes yet.</AnankeText>
                ) : null}
                {showHistory === true ? (
                <div className="grid w-full min-w-0 auto-rows-max content-start gap-ds-space-150">
                    {previousNotesSection === true && resolvedShowSortControl === true ? (
                        <div className="flex w-full min-w-0 items-center justify-between gap-x-ds-space-150">
                            <NotesPreviousNotesLabel />
                            <NotesSortControl
                                sortDirection={sortDirection}
                                onChange={setSortDirection}
                            />
                        </div>
                    ) : null}
                    {noteItems.map((item) => {
                        const canMutate = item.createdBy === workspaceUser.userId;
                        const isEditing = editingNoteId === item.id;
                        return (
                            <NoteFeedItem
                                key={item.id}
                                item={item}
                                canMutate={canMutate}
                                isEditing={isEditing}
                                truncateEnabled={isFullscreen === false}
                                onStartEdit={() => {
                                    setEditingNoteId(item.id);
                                    setEditMarkdown(item.bodyMarkdown);
                                    setEditAttachments(item.attachments);
                                }}
                                onCancelEdit={() => {
                                    setEditingNoteId(null);
                                    setEditMarkdown("");
                                    setEditAttachments([]);
                                }}
                                onDelete={() => setNotePendingDelete(item)}
                                editComposer={
                                    <NotesComposer
                                        composerId={editComposerId}
                                        value={editMarkdown}
                                        onChange={setEditMarkdown}
                                        stagedAttachments={editAttachments}
                                        onStagedAttachmentsChange={setEditAttachments}
                                        isSaving={savingExistingNoteId === item.id}
                                        isUploading={isUploadingEdit}
                                        onUploadFiles={(files) => void uploadFiles(files, setEditAttachments, setIsUploadingEdit)}
                                        showCancel
                                        onCancel={() => {
                                            setEditingNoteId(null);
                                            setEditMarkdown("");
                                            setEditAttachments([]);
                                        }}
                                        onSave={() =>
                                            saveMutation.mutate({
                                                markdown: editMarkdown,
                                                attachments: editAttachments,
                                                existing: item,
                                            })
                                        }
                                    />
                                }
                            />
                        );
                    })}
                </div>
                ) : null}
            </div>
            <NoteDeleteConfirmationModal
                opened={notePendingDelete !== null}
                onClose={() => setNotePendingDelete(null)}
                onConfirm={() => {
                    if (notePendingDelete !== null) {
                        deleteMutation.mutate(notePendingDelete);
                    }
                }}
                isDeleting={deleteMutation.isPending}
            />
        </div>
    );
}

const MOBILE_ASIDE_BREAKPOINT = "(max-width: 47.9375em)";

export function ListNotesSidePanel({context, onClose}: {context: ListNotesPanelContext; onClose: () => void}) {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [eventContainer, setEventContainer] = useState(context.eventContainer);
    const [isDirty, setIsDirty] = useState(false);
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT, false, {getInitialValueInEffect: true});

    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard: onClose});

    const headerActions = (
        <>
            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={isFullscreen === true ? ShrinkDiagonalIcon : GrowDiagonalIcon}
                label={isFullscreen === true ? "Collapse" : "Expand"}
                onClick={() => setIsFullscreen((current) => !current)}
            />
        </>
    );

    const body = (
        <ListNotesPanelBody
            context={context}
            eventContainer={eventContainer}
            onEventContainerPatched={setEventContainer}
            isFullscreen={isFullscreen}
            showHistory={context.showHistory ?? true}
            showSortControl={context.showHistory ?? true}
            onDirtyChange={setIsDirty}
        />
    );

    const panel =
        isMobile === true ? (
            isFullscreen === true ? (
                <FullscreenModal
                    headerLeft={<NotesPanelTitle />}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    body={body}
                    onClose={() => requestClose()}
                    bodyFillsViewport
                />
            ) : (
                <SidebarModal
                    headerLeft={<NotesPanelTitle />}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    body={body}
                    onClose={() => requestClose()}
                    bodyFillsViewport
                />
            )
        ) : isFullscreen === true ? (
            <AsideFullscreenPanel
                headerLeft={<NotesPanelTitle />}
                headerActions={headerActions}
                useStandardCloseButton={true}
                onCloseHeader={() => requestClose()}
                body={body}
                bodyFillsViewport
            />
        ) : (
            <AsideSidebarPanel
                headerLeft={<NotesPanelTitle />}
                headerActions={headerActions}
                useStandardCloseButton={true}
                onCloseHeader={() => requestClose()}
                body={body}
                bodyFillsViewport
            />
        );

    return (
        <>
            {panel}
            {modal}
        </>
    );
}
